/* ==========================================================================
   GM PURCHASE LIMIT — enforces a per-product max quantity per order.
   The limit comes from the product metafield custom.product_limit — the single
   source of truth — rendered onto the add-to-cart form as data-gm-limit and onto
   cart quantity inputs (and as the input max, which caps the +/- stepper).
   Two layers of enforcement:
     1. guardAdd  — blocks/clamps an add when the cart already holds the limit.
                    The stepper's max only caps ONE add; repeated Add-to-Cart
                    clicks would otherwise accumulate past the limit.
     2. enforce   — backstop: if a cart line ever exceeds its limit anyway, clamp
                    it back via the Cart API and tell the customer.
   No app required.
   ========================================================================== */
(function () {
  var BUSY = 'gm_limit_busy';
  var MSG = 'gm_limit_msg';

  function ensureStyle() {
    if (document.getElementById('gm-limit-style')) return;
    var s = document.createElement('style');
    s.id = 'gm-limit-style';
    s.textContent =
      '.gm-qty-limit{color:rgba(var(--color-foreground),0.6);font-weight:600;margin:0.4rem 0 0;}' +
      '.gm-limit-toast{position:fixed;left:50%;bottom:2.4rem;transform:translate(-50%,1rem);' +
      'background:#141b17;color:#fff;border:1px solid rgba(255,255,255,0.14);padding:1.1rem 1.7rem;' +
      'border-radius:10px;font-size:1.35rem;font-weight:600;z-index:1000;opacity:0;pointer-events:none;' +
      'transition:opacity .25s ease,transform .25s ease;box-shadow:0 14px 34px rgba(0,0,0,0.45);' +
      'max-width:90vw;text-align:center;}' +
      '.gm-limit-toast.is-visible{opacity:1;transform:translate(-50%,0);}';
    document.head.appendChild(s);
  }

  function toast(message) {
    ensureStyle();
    var t = document.getElementById('gm-limit-toast');
    if (!t) {
      t = document.createElement('div');
      t.id = 'gm-limit-toast';
      t.className = 'gm-limit-toast';
      t.setAttribute('role', 'status');
      document.body.appendChild(t);
    }
    t.textContent = message;
    void t.offsetWidth;
    t.classList.add('is-visible');
    clearTimeout(t._hide);
    t._hide = setTimeout(function () { t.classList.remove('is-visible'); }, 4200);
  }

  function findOverLimit() {
    var inputs = document.querySelectorAll('input[data-gm-limit]');
    for (var i = 0; i < inputs.length; i++) {
      var el = inputs[i];
      var limit = parseInt(el.getAttribute('data-gm-limit'), 10);
      var qty = parseInt(el.value, 10);
      if (!isNaN(limit) && !isNaN(qty) && qty > limit) {
        var id = el.getAttribute('data-quantity-variant-id') || el.getAttribute('data-index');
        if (id) return { id: String(id), limit: limit };
      }
    }
    return null;
  }

  function enforce() {
    if (sessionStorage.getItem(BUSY)) return;
    var over = findOverLimit();
    if (!over) return;
    sessionStorage.setItem(BUSY, '1');
    sessionStorage.setItem(MSG, 'Limit ' + over.limit + ' per order — we adjusted your cart.');
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: over.id, quantity: over.limit })
    })
      .then(function () { window.location.reload(); })
      .catch(function () { sessionStorage.removeItem(BUSY); sessionStorage.removeItem(MSG); });
  }

  /* ---- Add-to-cart enforcement -------------------------------------------
     The quantity stepper's max only caps a single add. Pressing Add/Pre-Order
     repeatedly adds the limit again each time, so the cart accumulates past it.
     Before an add is allowed through, compare what is already in the cart for
     that variant against the limit and clamp (or block) accordingly.
     ---------------------------------------------------------------------- */
  var cartQty = {}; // variant id -> quantity currently in the cart

  function refreshCartQty() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        cartQty = {};
        (cart.items || []).forEach(function (item) {
          var key = String(item.variant_id);
          cartQty[key] = (cartQty[key] || 0) + item.quantity;
        });
      })
      .catch(function () { /* offline — the cart-page backstop still clamps */ });
  }

  function guardAdd(e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;

    var host = form.closest('product-form[data-gm-limit]');
    if (!host) return;

    var limit = parseInt(host.getAttribute('data-gm-limit'), 10);
    var idInput = form.querySelector('[name="id"]');
    if (isNaN(limit) || !idInput) return;

    var variantId = String(idInput.value);
    var inCart = cartQty[variantId] || 0;
    var qtyInput = form.querySelector('input[name="quantity"]');
    var requested = qtyInput ? parseInt(qtyInput.value, 10) : 1;
    if (isNaN(requested) || requested < 1) requested = 1;

    var remaining = limit - inCart;

    if (remaining <= 0) {
      e.preventDefault();
      e.stopImmediatePropagation();
      toast('Limit ' + limit + ' per order — you already have ' + inCart + ' in your cart.');
      return;
    }

    if (requested > remaining) {
      // Let the add through, but only for what the limit still allows.
      if (qtyInput) qtyInput.value = remaining;
      requested = remaining;
      toast('Limit ' + limit + ' per order — we added ' + remaining + '.');
    }

    // Optimistic, then reconcile with the server.
    cartQty[variantId] = inCart + requested;
    setTimeout(refreshCartQty, 1200);
  }

  function init() {
    ensureStyle();
    sessionStorage.removeItem(BUSY);
    var pending = sessionStorage.getItem(MSG);
    if (pending) { sessionStorage.removeItem(MSG); toast(pending); }
    refreshCartQty();
    // Capture phase so this runs before Dawn's product-form submit handler.
    document.addEventListener('submit', guardAdd, true);
    enforce();
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  document.addEventListener('shopify:section:load', enforce);

  // Cart drawer opens / cart re-renders mutate the DOM — re-check (debounced).
  var raf;
  new MutationObserver(function () {
    clearTimeout(raf);
    raf = setTimeout(enforce, 250);
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
